<!-- AudioMeter.vue -->
<template>
    <div class="meter-container">
        <div
            v-for="level in 5"
            :key="level"
            class="meter-bar"
            :class="{
                'active': currentLevel >= level,
                'warning': level === 4 && currentLevel >= level,
                'danger': level > 4 && currentLevel >= level,
            }"
        ></div>
    </div>
</template>

<script>
export default {
    name: 'AudioMeter',
    props: {
        deviceId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            audioContext: null,
            mediaStream: null,
            analyser: null,
            dataArray: null,
            currentLevel: 0,
            animationFrame: null,
        };
    },
    async mounted() {
        await this.initializeAudio();
        this.startMonitoring();
    },
    watch: {
        async deviceId() {
            // Clean up existing audio resources
            this.stopMonitoring();
            // Initialize with new device
            await this.initializeAudio();
            this.startMonitoring();
        },
    },
    beforeUnmount() {
        this.stopMonitoring();
    },
    methods: {
        async initializeAudio() {
            try {
                this.audioContext = new (window.AudioContext || window.webkitAudioContext)();

                // Get media stream for the specific input device
                this.mediaStream = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        deviceId: {
                            // Request EXACT device, or fail. Because we're testing particular devices,
                            // it is important we don't fall back to a different device.
                            exact: this.deviceId,
                        },
                    },
                });

                // Create analyser node
                this.analyser = this.audioContext.createAnalyser();
                this.analyser.fftSize = 32;
                this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);

                // Connect the stream to the analyser
                const source = this.audioContext.createMediaStreamSource(this.mediaStream);
                source.connect(this.analyser);
            } catch (error) {
                if (error.name === 'NotAllowedError') {
                    this.$emit('audio-denied');
                } else {
                    console.error('Error initializing audio:', error);
                }
            }
        },
        startMonitoring() {
            if (! this.analyser) {
                return;
            }

            const updateMeter = () => {
                this.analyser.getByteFrequencyData(this.dataArray);

                // Calculate average volume level
                const average = this.dataArray.reduce((acc, val) => acc + val, 0) / this.dataArray.length;

                // Apply a logarithmic-like scaling to make it more sensitive to louder sounds
                // Using a modified exponential curve for better distribution
                const normalized = average / 255;
                const scaled = Math.pow(normalized, 0.6); // Using power of 0.6 makes it more sensitive to higher volumes

                // Adjust thresholds for each level
                let level;
                if (scaled < 0.05) level = 0;
                else if (scaled < 0.2) level = 1;
                else if (scaled < 0.4) level = 2;
                else if (scaled < 0.6) level = 3;
                else if (scaled < 0.75) level = 4;
                else level = 5;

                this.currentLevel = level;

                this.animationFrame = requestAnimationFrame(updateMeter);
            };

            updateMeter();
        },
        stopMonitoring() {
            if (this.animationFrame) {
                cancelAnimationFrame(this.animationFrame);
            }
            if (this.mediaStream) {
                this.mediaStream.getTracks().forEach(track => track.stop());
                this.mediaStream = null;
            }
            if (this.audioContext) {
                this.audioContext.close();
            }
        },
    },
};
</script>

<style scoped>
.meter-container {
    display: flex;
    gap: 4px;
    margin-bottom: 0.5rem;
}

.meter-bar {
    flex: 1;
    height: 10px;
    background: #e0e0e0;
    border-radius: 3px;
    transition: background-color 0.1s ease;
}

.meter-bar.active {
    background: #4CAF50;
}

.meter-bar.active.warning {
    background: #FFC107;
}

.meter-bar.active.danger {
    background: #f44336;
}
</style>
